/**
 * spaceShardConstants.ts
 * Tunable constants for the World 2 Constellation Shard collectible.
 *
 * Mirror of the World 1 platformer shard primitive (`src/constants/collectibleConstants.ts`'s
 * SHARD_* values), specialised for the auto-scroll shmup camera. Each
 * World 2 main level (2-1 .. 2-4) places 3 shards at fixed world-space
 * coordinates; collecting all 12 unlocks the 2-S secret stage via the
 * pre-existing {@link ../game/data/worldUnlock.isSecretStageUnlocked}
 * gate that already counts shards across the world's main levels.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module spaceShardConstants
 */

/** Logical hitbox width of a Constellation Shard in pixels. */
export const SPACE_SHARD_WIDTH_PX = 28;

/** Logical hitbox height of a Constellation Shard in pixels. */
export const SPACE_SHARD_HEIGHT_PX = 28;

/**
 * Maximum number of shards live in the scene at once. A W2 level
 * places at most {@link SPACE_SHARDS_PER_LEVEL} of them, so the pool
 * cap matches that — the scene never needs to spawn more than the
 * configured count for the active level.
 */
export const SPACE_SHARD_POOL_CAP = 12;

/**
 * Shards-per-main-level invariant. World 2 has 4 main levels (2-1 .. 2-4),
 * and the unlock gate ({@link ../game/data/worldUnlock.isSecretStageUnlocked})
 * already requires {@link ../game/data/worldProgress.SHARDS_PER_WORLD} = 12
 * total shards across the world. This constant is the per-level slice
 * (12 / 4 = 3) used to lay them out in {@link ../game/levels/spaceLevels}.
 */
export const SPACE_SHARDS_PER_LEVEL = 3;

/**
 * Pixels of camera-left margin past which an uncollected shard is
 * culled. Mirrors {@link ../constants/asteroidConstants.ASTEROID_OFFSCREEN_MARGIN}'s
 * intent — shards drift past once and don't respawn, so giving them a
 * margin matches the rest of the cull pipeline.
 */
export const SPACE_SHARD_OFFSCREEN_MARGIN_PX = 64;

/**
 * Sparkle animation cycle length (seconds). Drives a 2-frame palette
 * twinkle the renderer reads off `spaceState.elapsedSeconds` modulo
 * this value, so every shard sparkles in phase without per-instance
 * state.
 */
export const SPACE_SHARD_SPARKLE_PERIOD_S = 0.8;

/**
 * Pale-cyan body fill for the Constellation Shard greybox rect.
 * Distinct from {@link ../constants/colors.COLORS.GOLD} (#f5c842, star
 * seeds), {@link ../constants/rendering.SPACE_FIGHTER_LASER_COLOR}
 * (#aaff3a, ship laser), and {@link ../constants/rendering.SPACE_BOSS_PROJECTILE_COLOR}
 * (#ff3aa3, boss bolt) so the player reads it as a collectible — not
 * as a hazard or a weapon. Promote to {@link ../constants/colors.COLORS}
 * the moment a second caller needs the same hue.
 */
export const SPACE_SHARD_COLOR = '#6ee2ff';

/**
 * Sparkle accent colour swapped in on the bright half of the
 * {@link SPACE_SHARD_SPARKLE_PERIOD_S} cycle. Pure white reads as a
 * star-twinkle highlight against the {@link SPACE_SHARD_COLOR} body.
 */
export const SPACE_SHARD_SPARKLE_COLOR = '#ffffff';
